/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { EyeIcon, EyeOffIcon } from "lucide-react"
import type { ComponentProps } from "react"
import { useState } from "react"

import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "../primitives/input-group"
import { IconSwap } from "./icon-swap"

/**
 * A password input with a button that shows what was typed.
 *
 * It takes the input's props and renders no label, so it sits in a `Field` the way `Input` does.
 *
 * `className` styles the bordered box, which is the group rather than the input, so a caller sizes
 * it the way it sizes an `Input`. The input then fills whatever height the box has.
 */
export const PasswordInput = ({ className, ...props }: Omit<ComponentProps<typeof InputGroupInput>, "type">) => {
    const [masked, setMasked] = useState(true)

    return (
        <InputGroup className={className}>
            <InputGroupInput {...props} className="h-full" type={masked ? "password" : "text"} />
            <InputGroupAddon align="inline-end">
                <InputGroupButton
                    size="icon-xs"
                    aria-label="Show password"
                    aria-pressed={!masked}
                    // Kept out of the tab order, as before the migration, so Tab goes from the
                    // password straight to submit.
                    tabIndex={-1}
                    onClick={() => setMasked(!masked)}
                >
                    <IconSwap icons={[EyeIcon, EyeOffIcon]} active={masked ? 0 : 1} />
                </InputGroupButton>
            </InputGroupAddon>
        </InputGroup>
    )
}
